import React, { useState, useCallback } from 'react';
import {
  FaPaperPlane,
  FaStop,
  FaPlus,
} from 'react-icons/fa';
import AudioVisualizer from '../audio/AudioVisualizer';
import FeedbackModal from '../feedback/FeedbackModal';
import '../../styling/voiceChat.css';

/**
 * ChatControls – refactored by Dan Abramov ✨
 *
 * 1. Keeps a SINGLE <button> in the DOM so the element never unmounts between
 *    pointerdown and pointerup on touch devices. This fixes the «tap ‑ nothing
 *    happens» bug.
 * 2. Explicitly sets type="button" on every clickable <button> so no accidental
 *    form submit can swallow the event.
 * 3. Collapses the three mutually‑exclusive actions (send / stop / voice) into
 *    one stable handler decided at runtime.
 */
export default function ChatControls({
  userInput,
  setUserInput,
  handleSend,
  isRecording,
  isPlayingAudio,
  handleRecordToggle,
  handleStopPlayback,
  elapsedTime,
  model,
  isRetrieving,
  handleStopRetrieval,
  openVoiceAssistant,
}) {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const hasTyped = userInput.trim().length > 0;

  /** ------------------------------------------------------------------
   * Stable, single entry‑point for any of the three possible actions.
   * ------------------------------------------------------------------ */
  const handleActionClick = useCallback(() => {
    if (isRetrieving) {
      // console.log('Stopping retrieval...');
      handleStopRetrieval();
    } else if (hasTyped) {
      // console.log('Sending message...');
      handleSend();
    } else {
      // console.log('Opening voice assistant...');
      openVoiceAssistant();
    }
  }, [isRetrieving, hasTyped, handleSend, handleStopRetrieval, openVoiceAssistant]);

  /** Pressing «Enter» without Shift mirrors the action button. */
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (isRetrieving) {
          handleStopRetrieval();
        } else {
          handleSend();
        }
      }
    },
    [isRetrieving, handleStopRetrieval, handleSend]
  );

  /** Which icon should we display right now? */
  const renderIcon = () => {
    if (isRetrieving) return <FaStop size={20} />;
    if (hasTyped) return <FaPaperPlane size={20} />;
    return (
      <div className="voiceChatBars" aria-hidden>
        <span className="bar bar-1" />
        <span className="bar bar-2" />
        <span className="bar bar-3" />
      </div>
    );
  };

  return (
    <div className="bg-gradient-to-t from-green-50 to-white backdrop-blur-sm">
      {model !== 'openai-text-only' && (
        <AudioVisualizer
          isRecording={isRecording}
          isPlayingAudio={isPlayingAudio}
          elapsedTime={elapsedTime}
          onStopPlayback={handleStopPlayback}
          onStopRecording={handleRecordToggle}
        />
      )}

      <div className="p-3 max-w-3xl mx-auto w-full">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setShowFeedbackModal(true)}
            aria-label="Give daily feedback"
            className="p-1 text-gray-500 hover:text-gray-700"
          >
            <FaPlus size={18} />
          </button>

          <textarea
            rows={1}
            className="flex-1 border border-green-200 rounded-2xl px-3 py-2 resize-none focus:outline-none transition-shadow text-gray-900 leading-tight shadow-sm max-h-32 overflow-y-auto"
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
              // auto‑grow
              e.target.style.height = 'inherit';
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            onKeyDown={handleKeyDown}
            placeholder="Message News Buddy"
          />

          <button
            type="button"
            onClick={handleActionClick}
            aria-label={
              isRetrieving
                ? 'Stop Retrieval'
                : hasTyped
                ? 'Send Message'
                : 'Open Voice Assistant'
            }
            className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {renderIcon()}
          </button>
        </div>
      </div>

      {showFeedbackModal && <FeedbackModal onClose={() => setShowFeedbackModal(false)} />}
    </div>
  );
}
